import type { AccountActions } from "@/account/account-actions";
import { IAccountActions } from "@/account/account-actions";
import type {
  ICreditCard,
  IProviderConfig,
  IUserInfo,
} from "@/account/account-models";
import { ECreditCardFormType } from "@/account/account-models";
import { GetCreditsPurchaseHeader } from "@/account/get-credits-purchase/GetCreditsPurchaseHeader";
import { GetCreditsSubscriptionHeader } from "@/account/get-credits-subscription/GetCreditsSubscriptionHeader";
import type { IChatInfo } from "@/chats/chats-models";
import { updateMessageUnlockByPayingUsd } from "@/chats/messenger/messenger-action-creators";
import type { MessengerActions } from "@/chats/messenger/messenger-actions";
import { IMessengerActions } from "@/chats/messenger/messenger-actions";
import type { IMessageInfo } from "@/chats/messenger/messenger-models";
import { Button } from "@/common/button";
import { EButtonType } from "@/common/button/button-models";
import {
  ESpinnerSize,
  ESpinnerType,
} from "@/common/icons/icons-common/icons-loading-models";
import { LOADING_SPINNER } from "@/common/icons/icons-common/IconsLoading";
import type { FeedActions } from "@/feed/feed-actions";
import { IFeedActions } from "@/feed/feed-actions";
import type { IFeedInfo } from "@/feed/feed-models";
import type { IViewportDimensions } from "@/landing/landing-action-creators";
import { EPaymentType } from "@/models";
import type { PopupActions } from "@/popup/popup-actions";
import { IPopupActions } from "@/popup/popup-actions";
import type { RootState } from "@/store";
import type { IGetRequest, IPostRequest } from "@/utils/async";
import { isFetched } from "@/utils/async";
import { lazyWithRetry } from "@/utils/lazy";
import { omit } from "@/utils/object";
import { renderIfTrue } from "@/utils/react-conditional";
import { useDidMount, useDidUpdate } from "@/utils/react-lifecycle";
import { defined } from "@/utils/variable-evaluation";
import React from "react";
import { useDispatch, useSelector } from "react-redux";
import type { Location } from "react-router";
import { useLocation, useNavigate } from "react-router";
import type { Dispatch } from "redux";
import { bindActionCreators } from "redux";

// async components
const AsyncAccountGetCredits = lazyWithRetry(
  () => import("@/account/get-credits")
);

interface Props {
  location: Location;
  paymentType: EPaymentType;
  otherUser?: IUserInfo;
  chat?: IChatInfo;
  message?: IMessageInfo; // EPaymentType.ChatPurchase
  onUnlockUsingCredits?: () => void; // EPaymentType.ChatPurchase
  feedPost?: IFeedInfo;
  onClose: () => void;
  onPaymentSuccess?: () => void;
  // ---
  user?: IUserInfo;
  providerConfig?: IProviderConfig;
  viewportDimensions?: IViewportDimensions;
  getSavedCreditCards?: IGetRequest<any>;
  savedCreditCards?: ICreditCard[];
  postGetCredits?: IPostRequest<any, any>;
  accountActions?: AccountActions;
  feedActions?: FeedActions;
  messengerActions?: MessengerActions;
  popupActions?: PopupActions;
}

const PaymentPopupCore = React.memo(function PaymentPopupCore(props: Props) {
  const navigate = useNavigate();
  // Non-customers have no saved cards so the compact popup is extra friction,
  // except for Subscription which has contextual info worth showing.
  function shouldBypassCompact(): boolean {
    return (
      !!props.user &&
      !props.user.is_customer &&
      props.paymentType !== EPaymentType.Subscription
    );
  }

  function navigateToFullscreen() {
    const { paymentType, otherUser, chat, message, feedPost, location } = props;
    if (paymentType === EPaymentType.PackageSelect) {
      navigate("/account/get-credits", {
        state: { backTo: location.pathname },
      });
    } else if (paymentType === EPaymentType.Subscription) {
      navigate(`/account/subscribe-billing/${otherUser!._id}`, {
        state: { otherUser, backTo: location.pathname },
      });
    } else if (paymentType === EPaymentType.ChatTip) {
      navigate(`/account/chat-tip-billing/${otherUser!._id}/${chat!._id}`, {
        state: { otherUser },
      });
    } else if (paymentType === EPaymentType.ChatPurchase) {
      navigate(
        `/account/chat-purchase-billing/${otherUser!._id}/${chat!._id}/${message!._id}`,
        { state: { otherUser, message } }
      );
    } else if (paymentType === EPaymentType.FeedTip) {
      navigate(`/account/feed-tip-billing/${otherUser!._id}/${feedPost!._id}`, {
        state: { otherUser },
      });
    }
  }

  useDidMount(() => {
    if (shouldBypassCompact()) {
      props.onClose();
      navigateToFullscreen();
    }
  });

  useDidUpdate(props, (prevProps) => {
    if (
      prevProps.location.pathname !== props.location.pathname ||
      prevProps.location.search !== props.location.search
    ) {
      // on change chat

      props.onClose(); // close popup
    }
    if (
      !isFetched(prevProps.postGetCredits!) &&
      isFetched(props.postGetCredits!)
    ) {
      // after payment success

      if (props.paymentType === EPaymentType.ChatPurchase) {
        // update just bought message
        updateMessageUnlockByPayingUsd(
          props.postGetCredits!.responseData.message_data as IMessageInfo,
          props.messengerActions!
        );
      } else if (props.paymentType === EPaymentType.Subscription) {
        props.onPaymentSuccess?.();
      }
    }
  });

  const handleSuccessContinue = () => {
    props.onClose();
    if (
      props.paymentType === EPaymentType.Subscription &&
      !props.onPaymentSuccess
    ) {
      navigate(`/discover/full/${props.otherUser!._id}?new_sub=true`);
    }
  };

  if (shouldBypassCompact()) {
    return null;
  }

  const isSuccess = isFetched(props.postGetCredits!);

  return (
    <div data-test="payment-popup" data-payment-type={props.paymentType}>
      {renderIfTrue(!isSuccess, () => (
        <>
          {renderIfTrue(props.paymentType === EPaymentType.ChatPurchase, () => (
            <GetCreditsPurchaseHeader
              isCompact
              affiliate={props.otherUser!}
              message={props.message!}
            />
          ))}
          {renderIfTrue(props.paymentType === EPaymentType.Subscription, () => (
            <GetCreditsSubscriptionHeader affiliate={props.otherUser!} />
          ))}
          {renderIfTrue(
            props.paymentType === EPaymentType.PackageSelect,
            () => (
              <h2 className="mb-2.5 text-center text-heading-lg leading-6.5 font-bold text-title-color">
                Get Credits
              </h2>
            )
          )}
        </>
      ))}

      {renderIfTrue(
        defined(props.user) && defined(props.providerConfig),
        () => (
          <React.Suspense
            fallback={
              <>{LOADING_SPINNER(ESpinnerSize.Small)[ESpinnerType.Ios]}</>
            }
          >
            <AsyncAccountGetCredits
              paymentType={props.paymentType}
              formType={ECreditCardFormType.Compact}
              width={props.viewportDimensions!.width!}
              user={props.user!}
              providerConfig={props.providerConfig!}
              getSavedCreditCards={props.getSavedCreditCards!}
              savedCreditCards={props.savedCreditCards!}
              postGetCredits={props.postGetCredits!}
              affiliate={props.otherUser}
              chatId={props.chat ? props.chat._id : undefined}
              feedPostId={props.feedPost ? props.feedPost._id : undefined}
              message={props.message}
              accountActions={props.accountActions!}
              feedActions={props.feedActions!}
              messengerActions={props.messengerActions!}
              popupActions={props.popupActions!}
              onSuccessContinue={handleSuccessContinue}
            />
            {renderIfTrue(
              !isSuccess && props.paymentType === EPaymentType.ChatPurchase,
              // && props.user!.credits >=
              //   props.message!.price! *
              //     props.providerConfig!.usd_to_credit_ratio!,
              () => (
                <div className="mb-2.5">
                  <div className="my-5 flex items-center text-center text-body-lg font-bold text-body-text before:mr-1.5 before:flex-1 before:border-b before:border-border-neutral before:content-[''] after:ml-1.5 after:flex-1 after:border-b after:border-border-neutral after:content-['']">
                    or
                  </div>
                  <div>
                    <Button
                      onClick={props.onUnlockUsingCredits}
                      buttonType={EButtonType.BlueLink}
                    >
                      Unlock using Credits
                    </Button>
                  </div>
                </div>
              )
            )}
          </React.Suspense>
        )
      )}
    </div>
  );
});

export function PaymentPopup(
  props: Omit<
    Props,
    | "location"
    | "user"
    | "providerConfig"
    | "viewportDimensions"
    | "getSavedCreditCards"
    | "savedCreditCards"
    | "postGetCredits"
    | "accountActions"
    | "feedActions"
    | "messengerActions"
    | "popupActions"
  >
) {
  const location = useLocation();
  const user = useSelector((state: RootState) => state.accountStore.user);
  const providerConfig = useSelector(
    (state: RootState) => state.accountStore.providerConfig
  );
  const viewportDimensions = useSelector(
    (state: RootState) => state.landingStore.viewportDimensions
  );
  const getSavedCreditCards = useSelector(
    (state: RootState) => state.accountStore.getSavedCreditCards
  );
  const savedCreditCards = useSelector(
    (state: RootState) => state.accountStore.savedCreditCards
  );
  const postGetCredits = useSelector(
    (state: RootState) => state.accountStore.postGetCredits
  );
  const dispatch = useDispatch<Dispatch>();
  const accountActions = React.useMemo(
    () => bindActionCreators(omit(IAccountActions, "Type"), dispatch),
    [dispatch]
  );
  const feedActions = React.useMemo(
    () => bindActionCreators(omit(IFeedActions, "Type"), dispatch),
    [dispatch]
  );
  const messengerActions = React.useMemo(
    () => bindActionCreators(omit(IMessengerActions, "Type"), dispatch),
    [dispatch]
  );
  const popupActions = React.useMemo(
    () => bindActionCreators(omit(IPopupActions, "Type"), dispatch),
    [dispatch]
  );

  return (
    <PaymentPopupCore
      {...props}
      location={location}
      user={user as any}
      providerConfig={providerConfig as any}
      viewportDimensions={viewportDimensions}
      getSavedCreditCards={getSavedCreditCards}
      savedCreditCards={savedCreditCards}
      postGetCredits={postGetCredits}
      accountActions={accountActions}
      feedActions={feedActions}
      messengerActions={messengerActions}
      popupActions={popupActions}
    />
  );
}
